import type { TaskSummary } from "@common/types/task";
import type { LocalTask } from "./closeTemplateTasksDrawer.types";

export interface TaskLinkSelection {
    blockedByLocalIds: string[];
    relatedLocalIds: string[];
}

export const isTaskSavable = (task: LocalTask): boolean =>
    !task.isDeleted &&
    (task.persistedId !== undefined || task.name.trim().length > 0);

export function getBlockedDescendantIds(
    taskId: string,
    tasks: readonly LocalTask[],
): Set<string> {
    const blockedByBlocker = new Map<string, string[]>();
    for (const task of tasks) {
        if (task.isDeleted) {
            continue;
        }
        for (const blockerId of task.blockedByLocalIds) {
            blockedByBlocker.set(blockerId, [
                ...(blockedByBlocker.get(blockerId) ?? []),
                task.localId,
            ]);
        }
    }

    const descendants = new Set<string>();
    const pending = [taskId];
    for (let id = pending.pop(); id !== undefined; id = pending.pop()) {
        for (const blockedId of blockedByBlocker.get(id) ?? []) {
            if (!descendants.has(blockedId)) {
                descendants.add(blockedId);
                pending.push(blockedId);
            }
        }
    }
    return descendants;
}

export function getActiveBlockers(
    task: LocalTask,
    activeTasksById: ReadonlyMap<string, LocalTask>,
): TaskSummary[] {
    const blockers: TaskSummary[] = [];
    for (const blockerId of task.blockedByLocalIds) {
        const blocker = activeTasksById.get(blockerId);
        if (!blocker) {
            continue;
        }
        blockers.push({ id: blockerId, title: blocker.name });
    }
    return blockers;
}

export function getRelatedLocalIds(
    taskId: string,
    tasks: readonly LocalTask[],
): string[] {
    const ownRelatedIds = new Set(
        tasks.find((task) => task.localId === taskId)?.relatedLocalIds,
    );
    return tasks
        .filter(
            (task) =>
                !task.isDeleted &&
                task.localId !== taskId &&
                (ownRelatedIds.has(task.localId) ||
                    task.relatedLocalIds.includes(taskId)),
        )
        .map((task) => task.localId);
}

export function applyTaskLinks(
    tasks: LocalTask[],
    taskId: string,
    { blockedByLocalIds, relatedLocalIds }: TaskLinkSelection,
): LocalTask[] {
    const related = new Set(relatedLocalIds);
    return tasks.map((task) => {
        if (task.localId === taskId) {
            return {
                ...task,
                blockedByLocalIds: [...new Set(blockedByLocalIds)],
                relatedLocalIds: [...related],
            };
        }
        const withoutTask = task.relatedLocalIds.filter((id) => id !== taskId);
        return {
            ...task,
            relatedLocalIds: related.has(task.localId)
                ? [...withoutTask, taskId]
                : withoutTask,
        };
    });
}

export function deleteTasksWithLinks(
    tasks: LocalTask[],
    deletedIds: ReadonlySet<string>,
): LocalTask[] {
    return tasks.map((task) =>
        deletedIds.has(task.localId)
            ? {
                  ...task,
                  isDeleted: true,
                  blockedByLocalIds: [],
                  relatedLocalIds: [],
              }
            : {
                  ...task,
                  blockedByLocalIds: task.blockedByLocalIds.filter(
                      (id) => !deletedIds.has(id),
                  ),
                  relatedLocalIds: task.relatedLocalIds.filter(
                      (id) => !deletedIds.has(id),
                  ),
              },
    );
}
